import React, { useEffect } from "react";
import { useToggle } from "usehooks-ts";
import { useQueryClient } from "react-query";
import { config } from "config";
import {
  CommonErrorPopup,
  SocialMediaPill,
  SocialMediaPillType,
} from "component-library";
import { useTranslation } from "i18n";
import {
  AuthInstagramIntegrationCode,
  SocialMediaConnectionStatus,
  SocialMediaPlatformCode,
} from "shared-types";
import { SOCIAL_MEDIA_POST_MESSAGE_AUTH_COMPLETE } from "shared/constants";
import InstagramIntegrationPopUp from "./InstagramIntegrationPopUp";
import { socialMediaQueryKey } from "../constants";
import ReconnectInstagramPopUp from "./ReconnectInstagramPopUp";
import DisconnectInstagramPopUp from "./DisconnectInstagramPopUp";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { InstagramColoredIcon, CrossThickIcon } from "assets";
import ProfileButton from "pages/Profile/components/common/ProfileButton";

interface Props {
  isReadonly?: boolean;
  status?: SocialMediaConnectionStatus;
  instagramProfileUrl?: string;
  instagramAccountId: number;
  profileId: string;
  isProfileDesign?: boolean;
  isInstgramConnected?: boolean;
  isEdit?: boolean;
}

const InstagramIntegration = ({
  isReadonly,
  status,
  instagramProfileUrl,
  instagramAccountId,
  profileId,
  isProfileDesign,
  isInstgramConnected,
  isEdit,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [connectPopUpIsOpen, connectPopUpToggle, connectPopUpSetValue] =
    useToggle();
  const [disconnectPopUpIsOpen, disconnectPopUpToggle] = useToggle();
  const [reconnectPopUpIsOpen, reconnectPopUpToggle, reconnectPupUpSetValue] =
    useToggle();
  const [errorPopup, errorPopupToggle] = useToggle();

  const closeAllPopUps = () => {
    connectPopUpSetValue(false);
    reconnectPupUpSetValue(false);
  };

  useEffect(() => {
    const socialMediaIntegrationListener = async (event) => {
      const isInstagram =
        event?.data?.type === SOCIAL_MEDIA_POST_MESSAGE_AUTH_COMPLETE &&
        event?.data?.platformCode === SocialMediaPlatformCode.Instagram;

      if (event.origin !== config.domainOrigin || !isInstagram) return;

      if (event.data.success) {
        await queryClient.invalidateQueries(socialMediaQueryKey);

        return closeAllPopUps();
      }

      return errorPopupToggle();
    };

    window.addEventListener("message", socialMediaIntegrationListener);

    return () =>
      window.removeEventListener("message", socialMediaIntegrationListener);
  }, []);

  const isHidden =
    isReadonly &&
    (!status || status === SocialMediaConnectionStatus.RequiresReauth);

  if (isHidden) return null;

  const isConnected = status === SocialMediaConnectionStatus.Connected;
  const isDisconnectInstagramButtonVisible = isInstgramConnected && isEdit;
  const connectReconnectHandler =
    status === SocialMediaConnectionStatus.RequiresReauth
      ? reconnectPopUpToggle
      : connectPopUpToggle;

  return (
    <>
      {isProfileDesign ? (
        <ProfileCard className="group flex flex-1 cursor-pointer items-center justify-center p-2 hover:bg-white/20">
          <button
            onClick={
              isConnected
                ? () => window.open(instagramProfileUrl, "_blank")
                : connectReconnectHandler
            }
            className="flex flex-1 items-center justify-center gap-2 text-m dark:text-white/90"
          >
            <div className="absolute right-[6px] top-[5px]">
              {isDisconnectInstagramButtonVisible && (
                <ProfileButton
                  className="h-4 w-4 min-w-4 rounded-full p-[2px]"
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => {
                    event.stopPropagation();
                    disconnectPopUpToggle();
                  }}
                >
                  <CrossThickIcon height={16} width={16} />
                </ProfileButton>
              )}
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="transition-transform group-hover:scale-110">
                <InstagramColoredIcon height={20} width={20} />
              </span>
              {isInstgramConnected
                ? "Instagram"
                : t("performer:profile.socialMedia.instagram.popup.title")}
            </div>
          </button>
        </ProfileCard>
      ) : (
        <SocialMediaPill
          variant={SocialMediaPlatformCode.Instagram}
          type={
            isConnected
              ? SocialMediaPillType.Connected
              : SocialMediaPillType.Connect
          }
          isEdit={!isReadonly && isConnected}
          onClick={
            isConnected
              ? () => window.open(instagramProfileUrl, "_blank")
              : connectReconnectHandler
          }
          onRemoveClick={disconnectPopUpToggle}
          className="w-full"
        />
      )}
      {connectPopUpIsOpen && (
        <InstagramIntegrationPopUp
          close={connectPopUpToggle}
          profileId={profileId}
          isProfileDesign={isProfileDesign}
        />
      )}
      {reconnectPopUpIsOpen && (
        <ReconnectInstagramPopUp
          close={reconnectPopUpToggle}
          profileId={profileId}
          instagramAccountId={instagramAccountId}
        />
      )}
      {disconnectPopUpIsOpen && (
        <DisconnectInstagramPopUp
          disconnectPopUpToggle={disconnectPopUpToggle}
          instagramAccountId={instagramAccountId}
          profileId={profileId}
        />
      )}
      {errorPopup && (
        <CommonErrorPopup
          popupProps={{
            close: errorPopupToggle,
          }}
          alertMessageProps={{
            buttons: [
              {
                type: "primary",
                name: t("common:button.label.close"),
                click: errorPopupToggle,
              },
            ],
          }}
          errorCode={AuthInstagramIntegrationCode.Connect}
        />
      )}
    </>
  );
};

export default InstagramIntegration;
